import type React from 'react';
import { FixedSizeList } from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';

export interface DropdownListPageableProps<T> {
  heightList?: number;
  heightItem?: number;
  isLoading: boolean;
  isFetching: boolean;
  hasNextPage?: boolean;
  data: T[];
  placeholderComponent: React.ReactElement;
  loadingComponent: React.ReactElement;
  fetchNextPage: () => void;
  itemComponent: (item: T, index: number) => React.ReactNode;
}

const DropdownListPageable = <T,>({
  heightList = 300,
  heightItem = 40,
  isLoading,
  isFetching,
  hasNextPage,
  data,
  fetchNextPage,
  placeholderComponent: Placeholder,
  loadingComponent: Loading,
  itemComponent: ItemList,
}: DropdownListPageableProps<T>) => {
  const totalItems = data?.length;

  if (isLoading) return Loading;

  if (!totalItems) return Placeholder;

  const itemCount = hasNextPage ? data.length + 1 : data.length;

  const loadMoreItems = isFetching ? () => {} : fetchNextPage;

  const isItemLoaded = (index: number) => !hasNextPage || index < data.length;

  const Row = ({ index, style }: { index: number; style: any }) => {
    const item = data[index];
    const content = isItemLoaded(index) ? ItemList(item, index) : Loading;
    return <div style={style}>{content}</div>;
  };

  return (
    <InfiniteLoader
      isItemLoaded={isItemLoaded}
      itemCount={itemCount}
      loadMoreItems={loadMoreItems}
    >
      {({ onItemsRendered, ref }) => (
        <FixedSizeList
          className='scrollBar'
          itemCount={itemCount}
          onItemsRendered={onItemsRendered}
          ref={ref}
          height={heightList}
          itemSize={heightItem}
          width='100%'
        >
          {Row}
        </FixedSizeList>
      )}
    </InfiniteLoader>
  );
};

export default DropdownListPageable;
